// @flow
import { useEffect, useState } from 'react';

/**
 * @return true if the most recent user interaction was with a keyboard. This can be used to
 * customize :focus styles so components can be written in a way accessible to keyboard navigation
 * users, without altering the design while a pointer is being used.
 */
const useKeyboardNavigationMode = () => {
  // The current mode. We switch between keyboard and pointer mode based on the user's most recent
  // interaction.
  const [isKeyboardMode, setIsKeyboardMode] = useState<boolean>(false);

  useEffect(() => {
    // Switch to keyboard mode when a key is pressed
    const onKeyDown = (evt: KeyboardEvent) => {
      // Don't switch modes for modifier keys
      if (evt.metaKey || evt.altKey || evt.ctrlKey) {
        return;
      }

      setIsKeyboardMode(true);
    };

    // Switch to pointer mode when anything is clicked or tapped
    const onPointerDown = () => {
      setIsKeyboardMode(false);
    };

    /* eslint-disable no-restricted-globals */
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('touchstart', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
    /* eslint-enable no-restricted-globals */
  }, []);

  return isKeyboardMode;
};

export default useKeyboardNavigationMode;
